import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { BsCloudUpload } from "react-icons/bs";
import useUploadBulkImportFile from "api/BulkBookImport/useUploadBulkImportFile";
import useBulkImportLimits, {
	DEFAULT_BULK_IMPORT_LIMITS,
} from "api/BulkBookImport/useBulkImportLimits";
import StepPanel from "../StepPanel";
import StatusBanner from "../StatusBanner";
import FileChip from "../FileChip";

const CONCURRENCY = 3;
const NEED_MEDIA = "Upload at least one cover or PDF before you can submit.";

const guessType = (file) => {
	const name = (file.name || "").toLowerCase();
	if (name.endsWith(".pdf")) return "pdf";
	if (/\.(jpe?g|png|webp)$/.test(name)) return "cover";
	return null;
};

const UploadFilesStep = ({
	importId,
	existingUploads = [],
	onBack,
	onContinue,
	onUploaded,
	submitting = false,
	continueLabel = "Submit for processing →",
}) => {
	const upload = useUploadBulkImportFile();
	const { data: limits = DEFAULT_BULK_IMPORT_LIMITS } = useBulkImportLimits();
	const maxCoverBytes = (limits.max_cover_mb || 3) * 1024 * 1024;
	const maxPdfBytes = (limits.max_pdf_mb || 50) * 1024 * 1024;
	const dropzoneMaxSize = Math.max(maxCoverBytes, maxPdfBytes);
	const [queue, setQueue] = useState([]);
	const [uploading, setUploading] = useState(false);
	const [validationErrors, setValidationErrors] = useState([]);
	const [nudge, setNudge] = useState(false);
	const queueRef = useRef(queue);
	const autoStartedRef = useRef(new Set());
	const dropzoneRef = useRef(null);
	queueRef.current = queue;

	useEffect(() => {
		const handler = (event) => {
			if (uploading) {
				event.preventDefault();
				event.returnValue = "";
			}
		};
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [uploading]);

	const processQueue = useCallback(async () => {
		if (!importId) return;
		setUploading(true);

		const runOne = async (entry) => {
			setQueue((prev) =>
				prev.map((f) =>
					f.id === entry.id ? { ...f, status: "uploading", progress: 0, error: null } : f,
				),
			);
			try {
				await upload.mutateAsync({
					importId,
					file: entry.file,
					type: entry.type,
					onProgress: (pct) => {
						setQueue((prev) => prev.map((f) => (f.id === entry.id ? { ...f, progress: pct } : f)));
					},
				});
				setQueue((prev) =>
					prev.map((f) => (f.id === entry.id ? { ...f, status: "uploaded", progress: 100 } : f)),
				);
			} catch (error) {
				setQueue((prev) =>
					prev.map((f) =>
						f.id === entry.id
							? {
									...f,
									status: "failed",
									error: error?.response?.data?.message || "Upload failed",
								}
							: f,
					),
				);
			}
		};

		const toProcess = queueRef.current.filter(
			(f) => f.status === "waiting" || f.status === "failed",
		);

		for (let i = 0; i < toProcess.length; i += CONCURRENCY) {
			const batch = toProcess.slice(i, i + CONCURRENCY);
			await Promise.all(batch.map(runOne));
		}

		setUploading(false);
		onUploaded?.();
	}, [importId, upload, onUploaded]);

	const onDrop = useCallback(
		(accepted) => {
			const errors = [];
			const entries = [];
			accepted.forEach((file) => {
				const type = guessType(file);
				if (!type) {
					errors.push(`${file.name}: use a cover image (JPG, PNG, WebP) or PDF.`);
					return;
				}
				const maxBytes = type === "pdf" ? maxPdfBytes : maxCoverBytes;
				const maxMb = type === "pdf" ? limits.max_pdf_mb : limits.max_cover_mb;
				if (file.size > maxBytes) {
					errors.push(`${file.name}: ${type} files must be ${maxMb} MB or smaller.`);
					return;
				}
				entries.push({
					id: `${file.name}-${file.size}-${file.lastModified}`,
					file,
					type,
					status: "waiting",
					progress: 0,
					error: null,
				});
			});
			setValidationErrors(errors);
			if (entries.length) {
				setNudge(false);
				setQueue((prev) => [...prev, ...entries]);
			}
		},
		[maxCoverBytes, maxPdfBytes, limits.max_cover_mb, limits.max_pdf_mb],
	);

	const onDropRejected = useCallback(
		(rejections) => {
			const errors = rejections.map((r) => {
				const name = r.file?.name || "File";
				const tooBig = r.errors?.some((e) => e.code === "file-too-large");
				const badType = r.errors?.some((e) => e.code === "file-invalid-type");
				if (tooBig) {
					const type = guessType(r.file);
					if (type === "cover") {
						return `${name}: cover images must be ${limits.max_cover_mb} MB or smaller.`;
					}
					if (type === "pdf") {
						return `${name}: PDF files must be ${limits.max_pdf_mb} MB or smaller.`;
					}
					return `${name}: covers max ${limits.max_cover_mb} MB, PDFs max ${limits.max_pdf_mb} MB.`;
				}
				if (badType) {
					return `${name}: use a cover image (JPG, PNG, WebP) or PDF.`;
				}
				return `${name}: could not be added.`;
			});
			setValidationErrors(errors);
		},
		[limits.max_cover_mb, limits.max_pdf_mb],
	);

	useEffect(() => {
		if (!importId || uploading) return;
		const waiting = queue.filter((f) => f.status === "waiting");
		const fresh = waiting.filter((f) => !autoStartedRef.current.has(f.id));
		if (!fresh.length) return;
		fresh.forEach((f) => autoStartedRef.current.add(f.id));
		processQueue();
	}, [queue, importId, uploading, processQueue]);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		onDropRejected,
		multiple: true,
		maxSize: dropzoneMaxSize,
		accept: {
			"image/jpeg": [".jpg", ".jpeg"],
			"image/png": [".png"],
			"image/webp": [".webp"],
			"application/pdf": [".pdf"],
		},
	});

	const overall = useMemo(() => {
		if (!queue.length) return 0;
		const sum = queue.reduce((acc, f) => acc + (f.progress || 0), 0);
		return Math.round(sum / queue.length);
	}, [queue]);

	const covers = existingUploads.filter((u) => u.type === "cover");
	const pdfs = existingUploads.filter((u) => u.type === "pdf");
	const stagedCount = covers.length + pdfs.length;
	const queueDone = queue.length > 0 && queue.every((f) => f.status === "uploaded");
	const hasFailed = queue.some((f) => f.status === "failed");
	const canContinue = stagedCount > 0 || queueDone;
	const highlightMissing = nudge && !canContinue;

	useEffect(() => {
		if (canContinue) setNudge(false);
	}, [canContinue]);

	const handleBlockedSubmit = useCallback(() => {
		setNudge(true);
		toast.error(NEED_MEDIA);
		dropzoneRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "center",
		});
	}, []);

	return (
		<StepPanel
			stepNumber={3}
			title="Upload covers and PDFs"
			description="Upload the covers and PDFs you named in your book list. The name must match; the extension does not have to."
			status={
				canContinue ? (
					<StatusBanner
						tone="success"
						title={
							stagedCount > 0
								? `${covers.length} cover(s), ${pdfs.length} PDF(s) staged — ready to submit`
								: "Uploads finished — ready to submit"
						}
					/>
				) : highlightMissing ? (
					<StatusBanner tone="warning" title="Files still needed">
						Drop at least one cover image or PDF that matches a name in
						your book list, then submit.
					</StatusBanner>
				) : null
			}
			nav={{
				onBack,
				onNext: onContinue,
				onNextBlocked: handleBlockedSubmit,
				backLabel: "← Back",
				nextLabel: continueLabel,
				nextDisabled: !canContinue,
				nextDisabledReason: canContinue ? null : NEED_MEDIA,
				busy: uploading || submitting,
				busyLabel: submitting ? "Submitting…" : "Uploading…",
			}}
		>
			<div className="space-y-3">
				<div
					ref={dropzoneRef}
					{...getRootProps()}
					className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-5 text-center transition-all duration-200 ${
						isDragActive
							? "portal-upload-active border-akgreen bg-akgreen bg-opacity-10"
							: highlightMissing || validationErrors.length
								? "border-amber-400 bg-amber-50 bg-opacity-50 ring-2 ring-amber-200"
								: "border-gray-200 bg-gray-50 bg-opacity-80 hover:border-akgreen border-opacity-50"
					}`}
				>
					<input {...getInputProps()} />
					<div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-white text-akgreen shadow-sm">
						<BsCloudUpload className="h-4 w-4" aria-hidden />
					</div>
					<p className="text-sm font-semibold text-gray-900">
						{isDragActive
							? "Drop files here…"
							: highlightMissing
								? "Add covers or PDFs here to submit"
								: "Drop covers and PDFs here, or click to browse"}
					</p>
					<p className="mt-0.5 text-xs text-gray-500">
						JPG, PNG, WebP (max {limits.max_cover_mb} MB) · PDF (max{" "}
						{limits.max_pdf_mb} MB) · uploads start automatically
					</p>
				</div>

				{validationErrors.length > 0 ? (
					<div role="alert" aria-live="polite">
						<StatusBanner
							tone="warning"
							title="Some files could not be added"
							action={
								<button
									type="button"
									className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-50"
									onClick={() => setValidationErrors([])}
								>
									Dismiss
								</button>
							}
						>
							<ul className="mt-1 list-disc space-y-1 pl-4 text-sm">
								{validationErrors.map((msg) => (
									<li key={msg}>{msg}</li>
								))}
							</ul>
						</StatusBanner>
					</div>
				) : null}

				{queue.length > 0 ? (
					<div className="space-y-2 rounded-xl border border-gray-200 bg-white p-3">
						<div className="flex flex-wrap items-center justify-between gap-2">
							<div>
								<p className="text-sm font-semibold text-gray-900">
									Upload queue
								</p>
								<p className="text-xs text-gray-500">{overall}% overall</p>
							</div>
							{hasFailed ? (
								<button
									type="button"
									className="rounded-lg bg-akgreen px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
									disabled={uploading || !importId}
									onClick={processQueue}
								>
									{uploading ? "Uploading…" : "Retry failed"}
								</button>
							) : null}
						</div>
						<div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
							<div
								className="h-full rounded-full bg-akgreen transition-all duration-300"
								style={{ width: `${overall}%` }}
							/>
						</div>
						<ul className="max-h-40 space-y-2 overflow-y-auto">
							{queue.map((entry) => (
								<li key={entry.id}>
									<FileChip
										name={entry.file.name}
										type={entry.type}
										status={entry.status}
										progress={entry.progress}
										error={entry.error}
										sizeBytes={entry.file.size}
									/>
								</li>
							))}
						</ul>
					</div>
				) : null}

				{stagedCount > 0 ? (
					<div className="space-y-2">
						<div className="flex flex-wrap gap-2">
							<span className="rounded-full bg-akgreen bg-opacity-10 px-3 py-1 text-xs font-semibold text-akgreenest">
								{covers.length} cover(s) on server
							</span>
							<span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-800">
								{pdfs.length} PDF(s) on server
							</span>
						</div>
						<ul className="max-h-36 space-y-2 overflow-y-auto">
							{existingUploads.map((staged) => (
								<li key={staged.id}>
									<FileChip
										name={staged.original_name}
										type={staged.type}
										status="ready"
										sizeBytes={staged.size_bytes}
									/>
								</li>
							))}
						</ul>
					</div>
				) : null}
			</div>
		</StepPanel>
	);
};

export default UploadFilesStep;
